import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Briefcase, ExternalLink } from 'lucide-react';

export const Career = () => {
  const applicationOptions = [
    {
      type: 'internship',
      title: 'General Internship',
      description: 'Gain hands-on experience in tax and financial advisory',
      formUrl: 'https://docs.google.com/forms/d/1tk54k0kmcxM1HGUOLZJtkdQvNFWh-ktmTJRxFmBc4k4/viewform',
      icon: Briefcase,
      color: 'bg-secondary'
    },
    {
      type: 'digital-marketing',
      title: 'Digital Marketing Internship',
      description: 'Help us grow our online presence and digital footprint',
      formUrl: 'https://docs.google.com/forms/d/1InAEDktcIlMRZrsZB3eGz2EaR-2CtPAje1LQMlzGLmY/viewform',
      icon: Briefcase,
      color: 'bg-accent'
    },
    {
      type: 'full-time',
      title: 'Full Time Job',
      description: 'Join our team of experienced professionals',
      formUrl: 'https://forms.gle/Bf8aZUFJmmndHiLM7',
      icon: Briefcase,
      color: 'bg-primary'
    }
  ];

  const handleApplicationClick = (option) => {
    if (option.formUrl) {
      window.open(option.formUrl, '_blank');
    }
  };

  return (
    <div className="bg-background py-24 md:py-32" data-testid="career-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Join Our Team</p>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight font-playfair mb-6" data-testid="career-title">
            Career Opportunities
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl mx-auto">
            Join Jay Kothari and Company and be part of a legacy of excellence in tax and financial advisory services.
          </p>
        </div>

        {/* Application Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {applicationOptions.map((option, index) => (
            <Card 
              key={index} 
              className="hover:shadow-xl transition-all duration-300 cursor-pointer group" 
              onClick={() => handleApplicationClick(option)}
              data-testid={`career-option-${index}`}
            >
              <CardContent className="p-8 text-center">
                <div className={`${option.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <option.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 font-playfair">{option.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{option.description}</p>
                {option.formUrl ? (
                  <Button variant="outline" className="w-full group-hover:bg-secondary group-hover:text-white transition-colors">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Apply Now
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full">
                    Apply Below
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Why Join Us Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold font-playfair text-center mb-8">Why Join Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="text-center p-6" data-testid="career-benefit-1">
              <Briefcase className="h-10 w-10 mx-auto mb-4 text-secondary" />
              <h3 className="font-semibold mb-2">38+ Years Legacy</h3>
              <p className="text-sm text-muted-foreground">Learn from experienced professionals</p>
            </Card>
            <Card className="text-center p-6" data-testid="career-benefit-2">
              <Briefcase className="h-10 w-10 mx-auto mb-4 text-secondary" />
              <h3 className="font-semibold mb-2">Growth Opportunities</h3>
              <p className="text-sm text-muted-foreground">Continuous learning and development</p>
            </Card>
            <Card className="text-center p-6" data-testid="career-benefit-3">
              <Briefcase className="h-10 w-10 mx-auto mb-4 text-secondary" />
              <h3 className="font-semibold mb-2">Professional Environment</h3>
              <p className="text-sm text-muted-foreground">Work with a dedicated team</p>
            </Card>
          </div>
        </div>

        {/* Our Team Gallery */}
        <div className="mb-16" data-testid="team-gallery">
          <h2 className="text-3xl font-bold font-playfair text-center mb-3">Meet Our Team</h2>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get a glimpse of the people behind Jay Kothari and Company — a team driven by expertise, collaboration, and a shared passion for excellence.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { src: "https://customer-assets.emergentagent.com/job_kothari-services/artifacts/upmdp90m_WhatsApp%20Image%202026-04-14%20at%2018.25.21%20%281%29.jpeg", alt: "Our Partners" },
              { src: "https://customer-assets.emergentagent.com/job_kothari-services/artifacts/t7q93q78_WhatsApp%20Image%202026-04-14%20at%2018.25.21.jpeg", alt: "Team Celebrating Together" },
              { src: "https://customer-assets.emergentagent.com/job_kothari-services/artifacts/e1xuvblv_WhatsApp%20Image%202026-04-14%20at%2018.25.20%20%282%29.jpeg", alt: "Our Team at Work" },
              { src: "https://customer-assets.emergentagent.com/job_kothari-services/artifacts/mrf7j10s_WhatsApp%20Image%202026-04-14%20at%2018.25.22.jpeg", alt: "Our Team Members" },
            ].map((photo, idx) => (
              <div 
                key={idx} 
                className="overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
                data-testid={`team-photo-${idx}`}
              >
                <img 
                  src={photo.src} 
                  alt={photo.alt} 
                  className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Full Time Job Application */}
        <Card data-testid="career-form-section">
          <CardHeader>
            <CardTitle className="text-2xl font-playfair" data-testid="career-form-title">Full Time Job Application</CardTitle>
            <p className="text-sm text-muted-foreground">Click the button below to fill out our application form on Google Forms.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center py-6">
              <a href="https://forms.gle/Bf8aZUFJmmndHiLM7" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-lg px-8 py-6" data-testid="career-apply-fulltime-btn">
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Apply for Full Time Job
                </Button>
              </a>
            </div>
            <div className="p-4 bg-secondary/10 border border-secondary/20 rounded-lg text-center" data-testid="career-form-response-note">
              <p className="text-sm font-medium text-foreground">
                After submitting the form, our team will reach out to you within the next 3–7 days.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Alternatively, you may call us at <a href="tel:+919406563535" className="text-secondary font-semibold hover:underline">9406563535</a> for follow-up.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Career;
